import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, Sparkles, PartyPopper } from 'lucide-react';
import {
  TeamBApi,
  ApiError,
  type Plant,
  type ObservationResult,
  type AiObservationSnapshot,
  getSpeciesMismatch,
  formatDateTime,
} from '@ai-tiaozs/shared';
import { ImageUploader, PrimaryButton } from '@/components/ImageUploader';
import { VoiceInput } from '@/components/VoiceInput';
import { ObservationAiSummary } from '@/components/ObservationAiSummary';

type AiInference = {
  identify?: { species: string; confidence: string; healthStatus: string };
  disease?: {
    hasIssue: boolean;
    diseases: Array<{ name: string; severity: string; treatment: string }>;
    overallAssessment: string;
  };
};

function buildAiSnapshot(inference: AiInference | null): AiObservationSnapshot | undefined {
  if (!inference?.identify && !inference?.disease) return undefined;
  const hasPest = inference.disease?.hasIssue ?? false;
  let healthStatus = inference.identify?.healthStatus as AiObservationSnapshot['healthStatus'];
  if (hasPest && healthStatus === '健康') healthStatus = '亚健康';
  return {
    species: inference.identify?.species,
    confidence: inference.identify?.confidence as AiObservationSnapshot['confidence'],
    healthStatus,
    hasPestIssue: hasPest,
    pestSummary: hasPest
      ? inference.disease!.diseases.map((d) => `${d.name}(${d.severity})`).join('、')
      : undefined,
    pests: inference.disease?.diseases,
    overallAssessment: inference.disease?.overallAssessment,
  };
}

function mapLeafState(inference: AiInference | null): '正常' | '下垂' | '发黄' | '卷曲' | '有损伤' | '长新叶' {
  const h = inference?.identify?.healthStatus;
  if (h === '健康' && !inference?.disease?.hasIssue) return '正常';
  if (h === '病态' || inference?.disease?.hasIssue) return '有损伤';
  if (h === '亚健康') return '发黄';
  return '正常';
}

/**
 * B 队 · 拍照记录(王晟骅)
 */
export function TeamBDailyObservation() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const successRef = useRef<HTMLElement>(null);
  const [plants, setPlants] = useState<Plant[]>([]);
  const [plantId, setPlantId] = useState<string>('');
  const [photo, setPhoto] = useState<string | null>(null);
  const [uploadKey, setUploadKey] = useState(0);
  const [wateredToday, setWateredToday] = useState<'未浇水' | '正常浇水'>('未浇水');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<ObservationResult | null>(null);
  const [aiInference, setAiInference] = useState<AiInference | null>(null);

  useEffect(() => {
    TeamBApi.listPlants()
      .then((d) => {
        setPlants(d.plants);
        const q = params.get('plantId');
        if (q && d.plants.some((p) => p.id === q)) setPlantId(q);
        else if (d.plants[0]) setPlantId(d.plants[0].id);
      })
      .catch(() => setPlants([]));
  }, [params]);

  const handlePhotoChange = async (dataUrl: string | null) => {
    setPhoto(dataUrl);
    setAiInference(null);
    setSaved(null);
    setError(null);
    if (!dataUrl) return;
    setAnalyzing(true);
    try {
      const [identify, disease] = await Promise.all([
        TeamBApi.identifyPlant({ imageBase64: dataUrl }),
        TeamBApi.detectDisease({ imageBase64: dataUrl }),
      ]);
      setAiInference({ identify, disease });
    } catch {
      setAiInference(null);
    } finally {
      setAnalyzing(false);
    }
  };

  const resetForAnother = () => {
    setPhoto(null);
    setUploadKey((k) => k + 1);
    setAiInference(null);
    setSaved(null);
    setError(null);
    setNote('');
  };

  const selectedPlant = plants.find((p) => p.id === plantId);
  const pendingMismatch = getSpeciesMismatch(
    aiInference?.identify?.species,
    selectedPlant?.species,
  );

  const submit = async () => {
    if (!plantId) {
      setError('请先到添加植物页建档');
      return;
    }
    if (!photo) {
      setError('请上传一张植物照片');
      return;
    }
    setLoading(true);
    setError(null);
    setSaved(null);
    const snapshot = buildAiSnapshot(aiInference);
    try {
      const r = await TeamBApi.dailyObservation({
        plantId,
        photoDataUrl: photo,
        wateredToday,
        note,
        soil: '微湿',
        lastWatering: wateredToday === '正常浇水' ? '今天' : '昨天',
        lightPosition: '室内明亮处',
        leafState: mapLeafState(aiInference),
        growthChange: (aiInference?.disease?.overallAssessment ?? '暂无明显变化').slice(0, 500),
        aiSnapshot: snapshot,
      });
      setSaved(r);
      sessionStorage.setItem(
        'b_last_observation',
        JSON.stringify({ observationId: r.observationId, plantId }),
      );
      requestAnimationFrame(() => {
        successRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '提交失败');
    } finally {
      setLoading(false);
    }
  };

  if (plants.length === 0) {
    return (
      <div className="card text-sm text-slate-500">
        还没有植物,先去
        <Link to="/add" className="ml-1 text-teamB-primary underline">
          添加植物
        </Link>
        添加一个。
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-lg font-bold">📷 拍照记录</h1>
        <p className="text-xs text-slate-500">拍照后 AI 会识别种类和虫害,保存后可在诊断和日记里查看</p>
      </header>

      {!saved && (
        <section className="card space-y-3">
          <div>
            <label className="text-xs text-slate-500">① 选择植物</label>
            <select
              className="input mt-1"
              value={plantId}
              onChange={(e) => setPlantId(e.target.value)}
            >
              {plants.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} · {p.location}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs text-slate-500">② 拍一张植物照片(必填)</label>
            <div className="mt-1">
              <ImageUploader key={uploadKey} label="拍照" onChange={handlePhotoChange} />
            </div>
            {analyzing && (
              <p className="mt-1 flex items-center gap-1 text-[11px] text-teamB-primary">
                <Loader2 className="h-3 w-3 animate-spin" />
                AI 正在识别种类和病虫害…
              </p>
            )}
          </div>

          <div>
            <label className="text-xs text-slate-500">③ 今天浇了吗?</label>
            <div className="mt-1 flex gap-2">
              {(['未浇水', '正常浇水'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setWateredToday(v)}
                  className={`flex-1 rounded-md border px-3 py-2 text-sm transition ${
                    wateredToday === v
                      ? 'border-teamB-primary bg-teamB-primary/10 text-teamB-primary'
                      : 'border-slate-200 bg-white text-slate-600'
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-500">想说点啥(选填,可口述)</label>
            <div className="mt-1">
              <VoiceInput
                value={note}
                onChange={setNote}
                placeholder="比如:今天发现有两片新叶子,最下面一片有点发黄"
                maxLength={200}
                multiline
                rows={2}
                ariaLabel="补充"
              />
            </div>
          </div>

          {aiInference && (
            <div className="space-y-2 rounded-md border border-teamB-primary/30 bg-teamB-primary/5 p-3">
              <div className="flex items-center gap-1 text-xs font-medium text-teamB-primary">
                <Sparkles className="h-3 w-3" />
                AI 自动识别(保存后会一起存档)
              </div>
              <ObservationAiSummary snapshot={buildAiSnapshot(aiInference)} />
              {pendingMismatch && (
                <p className="rounded-md bg-teamA-warning/10 px-2 py-1.5 text-xs text-teamA-warning">
                  ⚠️ 照片识别为「{pendingMismatch.detected}」,与当前档案「{pendingMismatch.expected}」不一致。
                  请确认是否选对了植物,或先去添加对应植物。
                </p>
              )}
            </div>
          )}

          {error && <p className="text-xs text-teamA-danger">{error}</p>}

          <PrimaryButton loading={loading} loadingText="保存中…" onClick={submit}>
            保存今日记录
          </PrimaryButton>
        </section>
      )}

      {saved && (
        <section
          ref={successRef as React.RefObject<HTMLElement>}
          className="card space-y-3 border-emerald-300 bg-gradient-to-b from-emerald-50 to-white"
        >
          <div className="text-center">
            <PartyPopper className="mx-auto h-10 w-10 text-emerald-600" />
            <h2 className="mt-2 text-lg font-bold text-emerald-800">保存成功!</h2>
            <p className="text-sm text-slate-600">
              {saved.createdAt
                ? `${formatDateTime(saved.createdAt)} 记录已存档`
                : '记录已存档'}
              ,AI 识别结果已保存
            </p>
          </div>

          {saved.speciesMismatch && (
            <p className="rounded-md bg-teamA-warning/10 px-3 py-2 text-xs text-teamA-warning">
              ⚠️ 这条记录的照片像「{saved.speciesMismatch.detected}」,已挂到「
              {selectedPlant?.name}({saved.speciesMismatch.expected})」下,日记里会标记提醒。
            </p>
          )}

          {saved.aiSnapshot && <ObservationAiSummary snapshot={saved.aiSnapshot} />}

          <div className="rounded-md bg-white/80 p-3 text-sm">
            <p className="text-[11px] text-slate-500">AI 还观察到:</p>
            <ul className="mt-1 list-inside list-disc text-slate-700">
              {saved.visibleObservations.map((v, i) => (
                <li key={i}>{v}</li>
              ))}
            </ul>
            <p className="mt-2 text-[11px] text-slate-500">
              异常程度:
              <span className="badge ml-1 bg-teamB-primary/10 text-teamB-primary">
                {saved.abnormalLevel}
              </span>
            </p>
          </div>

          <button
            type="button"
            onClick={() => navigate(`/advice?obsId=${saved.observationId}`)}
            className="btn-primary w-full"
          >
            🩺 去看 AI 健康诊断 →
          </button>
          <button type="button" onClick={resetForAnother} className="btn-outline w-full text-sm">
            再拍一条记录
          </button>
        </section>
      )}
    </div>
  );
}
